import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, Bell, MessageCircle, UserPlus, MapPin, Check, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchNotifications();
  }, [user, navigate]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/api/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/notifications/read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (err) {
      console.error('Error marking read:', err);
    }
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await axios.put(`${API}/api/notifications/${notif.id}/read`, {}, { withCredentials: true }).catch(() => {});
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    }
    if (notif.activity_id) navigate(`/activity/${notif.activity_id}`);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'comment_reply': return <MessageCircle className="w-5 h-5 text-[#E07A5F]" />;
      case 'new_follower': return <UserPlus className="w-5 h-5 text-[#81B29A]" />;
      case 'nearby_activity': return <MapPin className="w-5 h-5 text-[#3B82F6]" />;
      default: return <Bell className="w-5 h-5 text-[#666B67]" />;
    }
  };

  const getMessage = (notif) => {
    switch (notif.type) {
      case 'comment_reply':
        return `${notif.actor_name} ${t('notif_replied')} "${notif.comment_text}"`;
      case 'new_follower':
        return `${notif.actor_name} ${t('notif_followed')}`;
      case 'nearby_activity':
        return `${t('notif_new_nearby')}: ${notif.activity_title}`;
      default:
        return notif.message || t('notif_generic');
    }
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return t('comment_just_now');
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center" data-testid="notif-back-btn">
              <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-[#2C302E] font-['Outfit']">{t('notif_title')}</h1>
              {unread > 0 && <p className="text-xs text-[#E07A5F]">{unread} {t('notif_unread')}</p>}
            </div>
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-sm text-[#E07A5F] font-medium" data-testid="mark-all-read-btn">
              <Check className="w-4 h-4 inline mr-1" />{t('notif_mark_read')}
            </button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-[#E07A5F]" />
            </div>
            <h2 className="text-xl font-semibold text-[#2C302E] mb-2">{t('notif_empty_title')}</h2>
            <p className="text-[#666B67]">{t('notif_empty_desc')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                className={`w-full flex items-start gap-3 p-4 rounded-2xl text-left transition-colors ${
                  notif.read ? 'bg-white' : 'bg-[#E07A5F]/5'
                }`}
                data-testid={`notification-${notif.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/5 flex items-center justify-center flex-shrink-0">
                  {getIcon(notif.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notif.read ? 'text-[#666B67]' : 'text-[#2C302E] font-medium'}`}>
                    {getMessage(notif)}
                  </p>
                  <p className="text-xs text-[#666B67] mt-1">{timeAgo(notif.created_at)}</p>
                </div>
                {!notif.read && <div className="w-2 h-2 rounded-full bg-[#E07A5F] flex-shrink-0 mt-2" />}
              </button>
            ))}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default Notifications;
